// Masquerade entry links.
//
// A masquerade token is minted once (POST /auth/v3/masquerade) and can be spent
// on either customer surface, so we build BOTH links from the same token rather
// than minting again per destination:
//
//   legacy app  — https://app.<env>?token=…        token read at the root, set
//                                                   as the access cookie
//   Hexagon     — https://app.hex.<env>/masq?token=…  dedicated /masq route
//                                                   (Hexagon PR #123)
//
// The two are NOT interchangeable: Hexagon has no root token handler, and the
// legacy app has no /masq route, so sending a staffer to the wrong one silently
// drops them at a login screen.
//
// Hosts are derived from the Honey host the staffer is already on, which is how
// this has always worked (honey.bambee.com → app.bambee.com,
// honey.stage.bambee.com → app.stage.bambee.com) — no new env wiring.

/**
 * Builds both masquerade URLs from one token.
 *
 * @param {object} params
 * @param {string} params.host        window.location.host (may include a port)
 * @param {string} params.protocol    window.location.protocol, e.g. "https:"
 * @param {string} params.accessToken masq JWT from /auth/v3/masquerade
 * @param {boolean} params.isHoneyUser true when the TARGET user is Bambee staff
 * @returns {{ app: string, hexagon: string|null }} hexagon is null when the
 *   target is a Honey user — they masquerade within Honey, and Hexagon is a
 *   customer surface, so offering a Hexagon link there would only mislead.
 */
export function buildMasqUrls({ host, protocol, accessToken, isHoneyUser }) {
  const scheme = protocol || 'https:';

  // Staff masquerading as staff stays on Honey — unchanged legacy behaviour.
  if (isHoneyUser) {
    return {
      app: `${scheme}//${host}?token=${accessToken}`,
      hexagon: null,
    };
  }

  const appHost = host.replace('honey', 'app');
  // app.bambee.com → app.hex.bambee.com, app.stage.bambee.com →
  // app.hex.stage.bambee.com. A host we can't pattern-match (local dev on
  // localhost:PORT) keeps the same origin so the /masq route is still
  // reachable against whatever is running there.
  const hexagonHost = appHost.startsWith('app.')
    ? appHost.replace(/^app\./, 'app.hex.')
    : appHost;

  return {
    app: `${scheme}//${appHost}?token=${accessToken}`,
    hexagon: `${scheme}//${hexagonHost}/masq?token=${accessToken}`,
  };
}
